import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { kioskApi, openKioskSignedDoc } from '../lib/kiosk-api.js';
import { ReferencePhotoStrip } from './ReferencePhotoStrip.jsx';

export function KioskTasksView({ session }) {
  const locKey = session.location.key;
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState('');

  async function load() {
    try {
      const r = await kioskApi('GET', `/api/kiosk/${locKey}/tasks`, null, session.token);
      setData(r);
    } catch (e) { setErr(e.message || String(e)); }
  }
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  // Task reference photos (CJ 2026-09-30). Hourly, NOT on the 30s poll: each
  // fetch mints fresh signed links, and new links would re-download every
  // image on the iPad twice a minute.
  const [photosByTask, setPhotosByTask] = useState({});
  useEffect(() => {
    const loadPhotos = () => kioskApi('GET', `/api/task-photos?location_id=${session.location.id}`, null, session.token)
      .then((r) => setPhotosByTask(r?.photos || {}))
      .catch((e) => console.warn('[kiosk] task photos failed:', e?.message || e));
    loadPhotos();
    const t = setInterval(loadPhotos, 60 * 60 * 1000);
    return () => clearInterval(t);
  }, [session.location.id, session.token]);

  async function complete(t) {
    setBusy(t.id); setErr('');
    try {
      await kioskApi('POST', `/api/kiosk/${locKey}/tasks/${t.id}/complete`, {}, session.token);
      await load();
    } catch (e) {
      // Server 403 ("Clock in before completing a task.") arrives as a
      // JSON body inside e.message — unwrap so the kiosk shows the
      // bare sentence, not a raw JSON blob.
      let msg = e?.message || String(e);
      try { const parsed = JSON.parse(String(msg)); if (parsed?.error) msg = parsed.error; } catch (_) {}
      setErr(msg);
    }
    finally { setBusy(null); }
  }

  if (err) return <div style={{ background: '#fee', padding: 12, borderRadius: 8, color: '#b91c1c', fontSize: 13 }}>{err}</div>;
  if (!data) return <div style={{ fontSize: 13, color: '#9e9e9e' }}>Loading…</div>;

  const { meta, tasks } = data;
  // Weekly day-of-week filter — same logic used by the main app's Daily
  // view, lifted into shared/task-recurrence.js. Uses meta.ymd from the
  // kiosk endpoint so the day matches what the server already considered
  // "today" for completion lookups. Tasks scheduled for other weekdays
  // disappear from the kiosk's list, totals, and empty state in one go.
  const visibleTasks = window.TaskRecurrence.filterByDay(tasks, meta.ymd);
  // Bucket tasks by period name. 'Anytime' rides above all named periods
  // and is always visible. Active period is visually highlighted; other
  // periods are dimmer. Empty period sections collapse so the kiosk
  // doesn't have a wall of "0 tasks".
  const periods = (meta.periods || []).slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  function bucketFor(t) {
    if (t.time_period_id) {
      const p = periods.find(p => p.id === t.time_period_id);
      if (p) return p.name;
    }
    const tp = (t.time_period || '').trim();
    if (tp && !/^(anytime|any|all\s*day)$/i.test(tp)) return tp;
    return 'Anytime';
  }
  const groups = [{ id: 'anytime', name: 'Anytime', start_time: '', end_time: '', isAnytime: true }];
  for (const p of periods) groups.push(p);
  const byBucket = new Map();
  for (const t of visibleTasks) {
    const k = bucketFor(t);
    if (!byBucket.has(k)) byBucket.set(k, []);
    byBucket.get(k).push(t);
  }
  const activePeriodId = meta.currentPeriod?.id ?? null;
  const totalOpen = visibleTasks.filter(t => !t.completion_id).length;
  const totalDone = visibleTasks.filter(t => t.completion_id).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Header */}
      <div style={{ background: '#fff', borderRadius: 8, padding: 12, border: '1px solid #e0e0e0' }}>
        <div style={{ fontSize: 11, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
          Today · {totalOpen} open · {totalDone} done
        </div>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#212121' }}>
          {meta.currentPeriod ? `${meta.currentPeriod.name} (${meta.currentPeriod.start_time}–${meta.currentPeriod.end_time})` : 'No active period right now'}
        </div>
        {!meta.currentPeriod && meta.nextPeriod && (
          <div style={{ fontSize: 11, color: '#757575' }}>
            {meta.nextPeriod.name} starts at {meta.nextPeriod.start_time}
          </div>
        )}
      </div>

      {visibleTasks.length === 0 && (
        <div style={{ textAlign: 'center', padding: 32, color: '#757575' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>☕</div>
          <div style={{ fontSize: 14 }}>No tasks scheduled for today.</div>
        </div>
      )}

      {groups.map(g => {
        const list = byBucket.get(g.name) || [];
        if (list.length === 0) return null;
        const isActive = g.isAnytime || (activePeriodId && g.id === activePeriodId);
        const open = list.filter(t => !t.completion_id);
        const done = list.filter(t => t.completion_id);
        return (
          <div key={g.name} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '4px 4px 0' }}>
              <span style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
                              color: isActive ? 'var(--forest, #1B2B1B)' : '#9e9e9e' }}>
                {g.name}
              </span>
              {!g.isAnytime && (g.start_time || g.end_time) && (
                <span style={{ fontSize: 10, color: '#bdbdbd' }}>
                  {g.start_time} – {g.end_time}
                </span>
              )}
              <span style={{ fontSize: 10, color: '#bdbdbd', marginLeft: 'auto' }}>
                {open.length} open · {done.length} done
              </span>
              {isActive && !g.isAnytime && (
                <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 999,
                                background: '#e8f5e9', color: '#2e7d32', textTransform: 'uppercase' }}>NOW</span>
              )}
            </div>
            {open.map(t => (
              <div key={t.id} style={{ background: '#fff', border: '1px solid #d0d4d9', borderRadius: 10, overflow: 'hidden',
                                          opacity: isActive ? 1 : 0.85 }}>
                <button onClick={() => complete(t)} disabled={busy === t.id}
                  style={{ width: '100%', background: 'transparent', border: 'none', padding: 14,
                            cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                            display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', border: '2px solid #d0d4d9',
                                 flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                 background: busy === t.id ? '#16a34a' : '#fff' }}>
                    {busy === t.id && <span style={{ color: '#fff', fontSize: 14 }}>⟳</span>}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#212121' }}>{t.title}</div>
                    {t.description && <div style={{ fontSize: 12, color: '#757575', marginTop: 2 }}>{t.description}</div>}
                    <div style={{ fontSize: 11, color: '#9e9e9e', marginTop: 4 }}>
                      {t.due_time ? `due ${t.due_time}` : ''}
                      {t.assigned_role ? ` · ${t.assigned_role}` : ''}
                    </div>
                  </div>
                </button>
                {(photosByTask[t.id] || []).length > 0 && (
                  <div style={{ borderTop: '1px solid #f3f4f6', padding: '10px 14px' }}>
                    <ReferencePhotoStrip photos={photosByTask[t.id]} size={96} />
                  </div>
                )}
                {Array.isArray(t.linked_documents) && t.linked_documents.length > 0 && (
                  <div style={{ borderTop: '1px solid #f3f4f6', padding: '8px 14px',
                                display: 'flex', gap: 6, flexWrap: 'wrap', background: '#fafafa' }}>
                    {t.linked_documents.map(d => (
                      <a key={d.id} href="#"
                        onClick={e => { e.preventDefault(); e.stopPropagation();
                          if (d.attachment_filename) openKioskSignedDoc(d.id, d.attachment_filename, session.token); }}
                        style={{ fontSize: 12, color: '#1d4ed8', background: '#eff6ff',
                                 padding: '4px 10px', borderRadius: 6, textDecoration: 'none',
                                 border: '1px solid #bfdbfe', fontWeight: 600 }}>
                        📎 {d.title}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {done.map(t => (
              <div key={t.id} style={{ background: '#f5f5f5', borderRadius: 10, padding: 14,
                                         display: 'flex', gap: 12, alignItems: 'flex-start', opacity: 0.55 }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#16a34a',
                               color: '#fff', fontSize: 16, lineHeight: '28px', textAlign: 'center', flexShrink: 0 }}>✓</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#212121', textDecoration: 'line-through' }}>{t.title}</div>
                  <div style={{ fontSize: 11, color: '#9e9e9e', marginTop: 2 }}>
                    by {t.completed_by_name || 'someone'}{t.completed_at ? ` · ${t.completed_at.slice(11, 16)}` : ''}
                  </div>
                </div>
                <button onClick={async () => {
                  if (!confirm('Undo this completion? It will return to the open list.')) return;
                  try {
                    await kioskApi('DELETE', `/api/kiosk/${locKey}/task-completions/${t.completion_id}`, null, session.token);
                    await load();
                  } catch (e) { setErr(e.message || String(e)); }
                }} style={{ flexShrink: 0, fontSize: 11, fontWeight: 600,
                              padding: '4px 10px', background: '#fff', color: '#92400e',
                              border: '1px solid #fde68a', borderRadius: 6, cursor: 'pointer',
                              fontFamily: 'inherit' }}>
                  Undo
                </button>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}
